import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Overline } from "@/components/Primitives";

export const InnerHero = ({ overline, title, subtitle, image }) => (
  <section className="relative min-h-[62vh] flex items-end overflow-hidden">
    <div className="absolute inset-0">
      <img src={image} alt={title} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/40 to-navy/30" />
    </div>
    <div className="relative z-10 max-w-[1400px] mx-auto w-full px-5 md:px-10 pb-16 pt-32">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-3xl"
      >
        <Overline className="text-gold">{overline}</Overline>
        <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1] tracking-tight text-ivory">
          {title}
        </h1>
        {subtitle && <p className="mt-5 sm:mt-6 max-w-2xl text-base sm:text-lg font-light text-ivory/80 leading-relaxed">{subtitle}</p>}
      </motion.div>
    </div>
  </section>
);

export const CTABand = ({
  overline = "Start Your Journey",
  title = "Let's craft your India journey",
  subtitle = "Tell us how you love to travel and we'll design a meaningful, premium and deeply relaxing journey — just for you.",
  primary = { label: "Plan Your Journey", to: "/contact" },
  secondary = { label: "Explore Experiences", to: "/experiences" },
}) => (
  <section className="grain-dark relative bg-teal text-ivory overflow-hidden">
    <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-32 text-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <Overline className="text-gold">{overline}</Overline>
        <h2 className="mt-5 font-serif font-light text-3xl sm:text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-4xl mx-auto">
          {title}
        </h2>
        <p className="mt-6 max-w-2xl mx-auto text-lg font-light text-ivory/80">{subtitle}</p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link
            to={primary.to}
            data-testid="ctaband-primary"
            className="w-full sm:w-auto justify-center inline-flex items-center gap-2 rounded-full bg-gold text-forest px-6 sm:px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:-translate-y-1"
          >
            {primary.label} <ArrowRight size={16} />
          </Link>
          {secondary && (
            <Link
              to={secondary.to}
              data-testid="ctaband-secondary"
              className="w-full sm:w-auto justify-center inline-flex items-center gap-2 rounded-full border border-ivory/40 text-ivory px-6 sm:px-8 py-4 text-sm font-semibold tracking-wide transition-colors hover:bg-ivory hover:text-forest"
            >
              {secondary.label}
            </Link>
          )}
        </div>
      </motion.div>
    </div>
  </section>
);
